<template>
    <div>
        <form
            ref="createPostForm"
            class="form create-post"
            :class="{ 'is-editing': isEditing }"
            @submit.prevent="handleSubmit"
            novalidate
        >
            <ValidationAlert v-if="errors && errors.length" :error-list="errors.collect('server')"></ValidationAlert>
            <div class="create-post__content">
                <img
                    :src="`/images/avatar/${user.id}`"
                    alt=""
                    class="avatar "
                    v-if="user"
                />
                <TextareaWithMentions
                    ref="postContent"
                    :default-value="formData.post_content"
                    v-model="formData.post_content"
                    field-name="post_content"
                    v-validate="'required'"
                />
                <button v-if="!isEditing" class="btn btn--icon btn--primary" :disabled="$wait.is('creating_post')">
                    <Spinner v-if="$wait.is('creating_post')"/>
                    <SvgIcon v-else icon="filled-send" classes="filled"/>
                </button>
            </div>
            <div class="form__validation">{{ errors.first('post_content') }}</div>
            <div class="create-post__actions">
                <ToggleButton
                    icon="filled-img"
                    :icon-filled="true"
                    label="Add Image"
                    :is-active="extraState.imageOpen"
                    :is-disabled="!!extraState.selectedRelated"
                    @click="extraState.imageOpen = !extraState.imageOpen"
                />
                <ToggleButton
                    icon="filled-file"
                    :icon-filled="true"
                    label="Add Files"
                    :is-active="extraState.attachmentsOpen"
                    :is-disabled="!!extraState.selectedRelated"
                    @click="extraState.attachmentsOpen = !extraState.attachmentsOpen"
                />
                <ToggleButton
                    icon="filled-oppomadd"
                    :icon-filled="true"
                    label="Link MADD/Opportunity"
                    :is-active="!!extraState.selectedRelated"
                    @click="handleSearch"
                />
                <ToggleButton
                    icon="filled-sector"
                    :icon-filled="true"
                    label="Add Subsector"
                    :is-active="extraState.sectorOpen"
                    :is-disabled="!!extraState.selectedRelated"
                    @click="extraState.sectorOpen = !extraState.sectorOpen"
                />
            </div>
            <div class="create-post__extra" v-if="Object.values(extraState).filter(Boolean).length">
                <div class="form__fieldset" v-if="extraState.imageOpen">
                    <FileUpload
                        ref="imageUpload"
                        id="image"
                        label="Image"
                        v-model="formData.image"
                        name="image"
                        :file-types="'image/*'"
                        :multiple="false"
                    />
                </div>
                <div class="form__fieldset" v-if="extraState.attachmentsOpen">
                    <FileUpload
                        ref="fileUpload"
                        id="files"
                        name="files"
                        label="Upload files relevant to this sector to share them with the rest of the team:"
                        v-model="formData.files"
                        :multiple="true"
                    />
                </div>
                <div v-if="extraState.selectedRelated">
                    <div class="form__group">
                        <span class="form__label">MADD/Opportunity</span>
                        <SelectedResult
                            :item="extraState.selectedRelated"
                            :has-remove="true"
                            v-on:remove="handleRelatedRemove"
                        />
                    </div>
                </div>
                <div class="form__fieldset" v-if="extraState.sectorOpen">
                    <FormControlDynamicSector
                        name="sectors"
                        ref="sectorDropdown"
                        :default-value="formData.sectors"
                        :default-items="formData.sectorOptions"
                        :parent-sector-id="sectorId"
                        :options="{ openDirection: 'below' }"
                        v-model="formData.sectors"
                    />
                </div>
            </div>
            <button id="update-button" v-if="isEditing" class="btn btn--outline">
                <Spinner
                    v-if="$wait.is('creating_post')"
                    color="#fff"
                    :width="18"
                    :height="18"
                />
                <span v-else>Update Post</span>
            </button>
        </form>
        <modal name="related-item-search">
            <SearchModalForm
                :sectorId="sectorId"
                v-on:change="handleRelatedSelection"
            />
        </modal>
    </div>
</template>
<script>
import { mapState } from 'vuex';
import API from '@api';
import SvgIcon from '@components/SvgIcon';
import SectorViewFilters from '@components/Sector/SingleView/Filters';
import FileUpload from '@components/Form/FileUpload';
import SearchModalForm from '@components/SearchModal/Form';
import SearchModalResult from '@components/SearchModal/Result';
import SelectedResult from '@components/SearchModal/SelectedResult';
import UserCard from '@components/User/UserCard';
import TextareaWithMentions from '@components/Form/TextareaWithMentions';
import ToggleButton from '@components/ToggleButton';
import Spinner from '@components/Spinner';
import FormControlDynamicSector from '@components/Form/Control/FormControlDynamicSector';
import ValidationAlert from '@components/ValidationAlert';
import Form from '@components/Form/Form';

export default {
    name: 'CreatePost',
    mixins: [Form],
    components: {
        ValidationAlert,
        FormControlDynamicSector,
        Spinner,
        ToggleButton,
        TextareaWithMentions,
        UserCard,
        FileUpload,
        SectorViewFilters,
        SvgIcon,
        SelectedResult,
        SearchModalResult,
        SearchModalForm,
    },
    props: {
        sectorId: {
            type: Number,
            default: null,
        },
        defaultValues: {
            type: Object,
            default: () => ({}),
        },
        isEditing: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            formData: {
                post_content: this.defaultValues.post_content ?? '',
                sectors: this.defaultValues.sectors ?? [this.sectorId],
                sectorOptions: this.defaultValues.sectorOptions ?? null,
                image: this.defaultValues.image ?? null,
                files: this.defaultValues.files ?? [],
                related_item: this.defaultValues.related_item,
            },
            extraState: {
                sectorOpen: (
                    !!((this.defaultValues.sectors ?? []).filter((sec) => sec !== this.sectorId).length) &&
                    !(this.defaultValues.related_item)
                ),
                imageOpen: !!(this.defaultValues.image ?? false),
                attachmentsOpen: !!((this.defaultValues.files ?? []).length),
                selectedRelated: this.defaultValues.related_item,
            },
        }
    },
    methods: {
        handleSubmit() {
            this.$emit('form-submit', this.formData);
        },
        handleSearch() {
            this.$modal.show('related-item-search');
        },
        handleRelatedSelection: function(value) {
            this.extraState = {
                sectorOpen: false,
                imageOpen: false,
                attachmentsOpen: false,
                selectedRelated: value,
            };

            this.formData = {
                post_content: this.formData.post_content,
                related_item: value,
                files: [],
                image: null,
                sectors: [],
            }

            this.$modal.hide('related-item-search');
        },
        handleRelatedRemove: function() {
            this.extraState.selectedRelated = null;
            this.formData.related_item = null;
        },
        updateErrors: function(error) {
            API.mapServerValidation(error.response, this.$validator);
        },
        startWait: function() {
            this.$wait.start('creating_post');
        },
        endWait: function() {
            this.$wait.end('creating_post');
        },
        resetForm: function() {
            if(this.$refs.postContent) {
                this.$refs.postContent.reset();
            }

            if(this.$refs.fileUpload) {
                this.$refs.fileUpload.clearAll();
            }

            if(this.$refs.imageUpload) {
                this.$refs.imageUpload.clearAll();
            }

            if(this.$refs.sectorDropdown) {
                this.$refs.sectorDropdown.clear();
            }

            this.formData = {
                post_content: null,
                sectors: [this.sectorId],
                defaultItems: null,
                image: null,
                sectorOptions: null,
                files: [],
                related_item: null,
            };

            this.extraState = {
                sectorOpen: false,
                imageOpen: false,
                attachmentsOpen: false,
                selectedRelated: null,
            }
        },
    },
    computed: {
        ...mapState({
            user: state => state.auth.user,
        }),
        defaultSectorIds: function() {
            return (this.formData.sectors ?? []).map((sec) => sec.id);
        },
    },
}
</script>
<style lang="scss" scoped>

.form__validation {
    padding-left: 4.6rem;
    padding-top: .6rem;
}

.create-post {
    padding: 2rem;
    border-radius: 4px;
    box-shadow: 0 6px 20px 0 rgba(49, 49, 74, 0.16);

    .create-post__content {
        position: relative;
        display: flex;
        align-items: flex-start;
        padding-right: 1.7rem;

        .avatar {
            height: 3.6rem;
            width: 3.6rem;
        }

        .btn {
            position: absolute;
            top: 0;
            right: 0;
        }
    }

    .create-post__actions {
        margin-top: 1.5rem;
        margin-bottom: 1rem;
        display: flex;
        column-gap: 1rem;
        align-items: center;
        justify-content: space-between;

        &:last-child {
            margin-bottom: 0;
        }

        .btn + .btn {
            margin-left: 0;
        }
    }

    .create-post__extra {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        row-gap: 2rem;

        > * {
            width: 100%;
        }
    }

    .create-post__toggle {}

    &.is-editing {
        .create-post__content {
            padding-right: 0;

            .quillWrapper .ql-editor {
                border-radius: 1.8rem;
            }
        }
    }
}

#update-button {
    margin-top: 2rem;
}
</style>

<style lang="scss">
.create-post {
    .vue-treeselect__menu-container,
    .vue-treeselect__menu-container .vue-treeselect__menu {
        position: relative;
    }
}
</style>
